/* DockDash: interaction layer.
   The 3D viewer and its one camera sequence.
   Everything here is progressive enhancement: the page works without this file. */
(function () {
  'use strict';

  var root = document.documentElement;

  function motionOk() { return root.classList.contains('motion-ok'); }
  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
  function ease(t) { t = clamp(t, 0, 1); return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function wrap180(d) { return ((d + 540) % 360) - 180; }
  var FILM_SPEED = 1.35;                    // plays the camera sequence 35% faster; its timings below stay in sequence seconds

  /* ---------- 3D viewer ----------
     Renders the real prototype geometry (v037 non-metal CAD, assets/visordock-model.json: the six printed
     parts exactly as exported) with a small WebGL renderer, no library. The thumb lever turns the comb
     about the axle at (8, 55.5) mm; each card moves out by the same formula as the CAD's reference cards.
     Nothing is exaggerated for effect: lever travel is 0 to 35 degrees and the cards follow it. */
  var MAX_ANGLE = 35;                      // lever travel, degrees (CAD: closed 0 / open 35)
  var PIVOT = [8 - 52, 33 - 55.5];         // lever axle in viewer coordinates (mm)
  var CARD_SIZE = [85.6, 53.98, 0.76];     // nominal card, mm
  var CARD_REST = 104 - 101.6;             // cards rest this far inside the case end, mm
  var GROUP_OF = { housing: 'case', cover: 'case', lever: 'lever', comb: 'lever', retainer: 'lever', hook: 'hook' };
  /* Shown in gold so the geometry reads against the black studio (a render colour, not a product option); neutral cards so the three lanes read apart. */
  var PART_COLORS = { housing: [186, 158, 100], cover: [178, 151, 95], hook: [198, 169, 107], comb: [204, 176, 114], lever: [222, 196, 138], retainer: [210, 183, 124] };
  var LETTER_COLOR = [12, 12, 12];
  var FLUSH_LETTERING = false;             // the v037 CAD's recessed lettering reads CRUISECLIP, so it is drawn in black
  var CARD_COLORS = [[20, 20, 20], [58, 59, 61], [236, 236, 234]];   // edges of the black, graphite and white sample cards
  var LEVER_AT = [-38, -22, -1];           // centre of the thumb lever, for the close view

  /* Named views for the Closed / Open buttons */
  var VIEWS = {
    closed: { rx: -22, ry: -28, open: 0 },
    open: { rx: -22, ry: -28, open: 1 }
  };

  /* The camera sequence, in seconds. Each key is a held composition; the camera eases between them.
     Overview, close on the recessed lever, the lever runs its full 0-35 degree travel while the camera
     draws back to the card end, then a final view of the whole product. */
  var FILM = [
    { t: 0, rx: -16, ry: -58, zoom: 1.18, at: [22, 4, 0] },
    { t: 3.6, rx: -20, ry: -36, zoom: 1.04, at: [0, 0, 0] },
    { t: 5.8, rx: -30, ry: -14, zoom: 0.56, at: LEVER_AT },
    { t: 7.6, rx: -28, ry: -8, zoom: 0.53, at: LEVER_AT },
    { t: 10.6, rx: -22, ry: -42, zoom: 0.86, at: [18, 0, 0] },
    { t: 13.2, rx: -22, ry: -28, zoom: 1, at: [0, 0, 0] },
    { t: 14.6, rx: -8, ry: 96, zoom: 2, at: [0, 100, -70] },
    { t: 20.4, rx: -8, ry: 96, zoom: 2, at: [0, 100, -70] },
    { t: 22.4, rx: -22, ry: -28, zoom: 1, at: [0, 0, 0] }
  ];
  var MOUNT_RUN = [0.5, 2.9];              // the case slides onto the U-dock on the visor
  var FOLD_UP = [15.2, 17.6], FOLD_DOWN = [18.6, 20.6];   // the visor folds up to the roof, then back down
  var LEVER_RUN = [6.2, 10.8];             // the lever turns 0 -> 35 degrees over this span
  var FILM_END = 22.8;

  function filmAt(t) {
    var i = 0;
    while (i < FILM.length - 2 && t > FILM[i + 1].t) i++;
    var a = FILM[i], b = FILM[i + 1], k = ease((t - a.t) / (b.t - a.t));
    function mix(x, y) { return x + (y - x) * k; }
    return {
      rx: mix(a.rx, b.rx), ry: mix(a.ry, b.ry), zoom: mix(a.zoom, b.zoom),
      at: [mix(a.at[0], b.at[0]), mix(a.at[1], b.at[1]), mix(a.at[2], b.at[2])],
      open: ease((t - LEVER_RUN[0]) / (LEVER_RUN[1] - LEVER_RUN[0])),
      mount: ease((t - MOUNT_RUN[0]) / (MOUNT_RUN[1] - MOUNT_RUN[0])),
      fold: ease((t - FOLD_UP[0]) / (FOLD_UP[1] - FOLD_UP[0])) - ease((t - FOLD_DOWN[0]) / (FOLD_DOWN[1] - FOLD_DOWN[0]))
    };
  }

  /* Card travel past its resting place, in mm (same formula as the CAD's reference cards; cards rest 2.4 mm inside the case). */
  function cardLift(lane, angle) {
    var rad = 22.5 + 12 * lane;
    return 7.8 * (Math.cos(angle) - 1) + rad * Math.sin(angle);
  }

  /* ----- small matrix helpers (column-major) ----- */
  function ident() { var m = new Float32Array(16); m[0] = m[5] = m[10] = m[15] = 1; return m; }
  function mul(a, b) {
    var o = new Float32Array(16);
    for (var c = 0; c < 4; c++) for (var r = 0; r < 4; r++) {
      var s = 0;
      for (var k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k];
      o[c * 4 + r] = s;
    }
    return o;
  }
  function trans(x, y, z) { var m = ident(); m[12] = x; m[13] = y; m[14] = z; return m; }
  function rotX(a) { var c = Math.cos(a), s = Math.sin(a), m = ident(); m[5] = c; m[6] = s; m[9] = -s; m[10] = c; return m; }
  function rotY(a) { var c = Math.cos(a), s = Math.sin(a), m = ident(); m[0] = c; m[2] = -s; m[8] = s; m[10] = c; return m; }
  function rotZ(a) { var c = Math.cos(a), s = Math.sin(a), m = ident(); m[0] = c; m[1] = s; m[4] = -s; m[5] = c; return m; }
  function persp(fovy, aspect, near, far) {
    var t = 1 / Math.tan(fovy / 2), m = new Float32Array(16);
    m[0] = t / aspect; m[5] = t; m[10] = (far + near) / (near - far); m[11] = -1; m[14] = 2 * far * near / (near - far);
    return m;
  }

  /* ----- geometry ----- */
  function b64(str, Type) {
    var bin = atob(str), n = bin.length, bytes = new Uint8Array(n);
    for (var i = 0; i < n; i++) bytes[i] = bin.charCodeAt(i);
    return new Type(bytes.buffer);
  }

  /* Flat-shaded triangles: 6 floats per vertex (position, face normal). */
  function flatten(pos, idx, scale) {
    var out = new Float32Array(idx.length * 6);
    for (var t = 0; t < idx.length; t += 3) {
      var a = idx[t] * 3, b = idx[t + 1] * 3, c = idx[t + 2] * 3;
      var ax = pos[a] * scale, ay = pos[a + 1] * scale, az = pos[a + 2] * scale;
      var bx = pos[b] * scale, by = pos[b + 1] * scale, bz = pos[b + 2] * scale;
      var cx = pos[c] * scale, cy = pos[c + 1] * scale, cz = pos[c + 2] * scale;
      var ux = bx - ax, uy = by - ay, uz = bz - az, vx = cx - ax, vy = cy - ay, vz = cz - az;
      var nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      var l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
      nx /= l; ny /= l; nz /= l;
      var o = t * 6;
      out[o] = ax; out[o + 1] = ay; out[o + 2] = az; out[o + 3] = nx; out[o + 4] = ny; out[o + 5] = nz;
      out[o + 6] = bx; out[o + 7] = by; out[o + 8] = bz; out[o + 9] = nx; out[o + 10] = ny; out[o + 11] = nz;
      out[o + 12] = cx; out[o + 13] = cy; out[o + 14] = cz; out[o + 15] = nx; out[o + 16] = ny; out[o + 17] = nz;
    }
    return out;
  }

  /* A card with rounded corners (ISO/IEC 7810 ID-1: 3.18 mm radius), centred, faces on +z/-z; outline runs anticlockwise seen from +z. */
  function roundedCardData(w, h, d, r) {
    var pts = [], seg = 8, x = w / 2 - r, y = h / 2 - r, z = d / 2;
    [[x, y, 0], [-x, y, 90], [-x, -y, 180], [x, -y, 270]].forEach(function (c) {
      for (var s = 0; s <= seg; s++) { var a = (c[2] + 90 * s / seg) * Math.PI / 180; pts.push([c[0] + r * Math.cos(a), c[1] + r * Math.sin(a)]); }
    });
    var n = pts.length, pos = [0, 0, z, 0, 0, -z], idx = [];
    pts.forEach(function (p) { pos.push(p[0], p[1], z, p[0], p[1], -z); });
    for (var i = 0; i < n; i++) {
      var j = (i + 1) % n, ti = 2 + i * 2, tj = 2 + j * 2;
      idx.push(0, ti, tj, 1, tj + 1, ti + 1, ti + 1, tj + 1, tj, ti + 1, tj, ti);
    }
    return flatten(pos, idx, 1);
  }

  /* The sun visor the holder is shown on: a typical 330 x 165 x 20 mm cloth visor. Its end edge sits in the U-dock's gap
     (viewer x -26, z -27.2 to -7.2, against the 20 mm hook opening), the lever end of the case stays clear past that edge,
     and a closed vanity mirror flap sits on the driver's side further along. Illustrative, not a particular car. */
  var VISOR = [330, 165, 20];
  /* Folding up: the visor turns about its hinge along the top edge (viewer y 123, z -17.2), swinging its free edge toward
     the windscreen. The face the holder is on ends up facing down into the car, so the holder and anything clipped to it
     hang below the folded visor, never between it and the roof. The roof lining sits 17 mm above the hinge line. */
  var HINGE = [123, -17.2], FOLD_ANGLE = 84, ROOF_Y = 140, ROOF_COLOR = [38, 39, 42];
  var CASE_SLIDE = 70;                     // the case slides this far along its rail onto the U-dock as it clips on
  function foldMatrix(k) {
    return mul(trans(0, HINGE[0], HINGE[1]), mul(rotX(k * FOLD_ANGLE * Math.PI / 180), trans(0, -HINGE[0], -HINGE[1])));
  }
  /* The roof lining: one face, facing down, so it shows from inside the car and vanishes from a camera above it. */
  function roofData() {
    var y = ROOF_Y, x0 = -1400, x1 = 1700, z0 = -1600, z1 = 900;   // big enough that its edges stay out of shot
    return flatten([x0, y, z0, x1, y, z0, x1, y, z1, x0, y, z1], [0, 1, 2, 0, 2, 3], 1);
  }
  var VISOR_COLOR = [124, 126, 130], FLAP_COLOR = [70, 72, 76];
  function moved(d, x, y, z) { for (var i = 0; i < d.length; i += 6) { d[i] += x; d[i + 1] += y; d[i + 2] += z; } return d; }
  /* Grey woven headliner cloth, drawn here and tiled every 30 mm. */
  function fabricTexture(gl) {
    var c = document.createElement('canvas'); c.width = c.height = 256;
    var x = c.getContext('2d'), img = x.createImageData(256, 256), seed = 7;
    function rnd() { seed = (seed * 16807) % 2147483647; return seed / 2147483647; }
    for (var py = 0; py < 256; py++) for (var px = 0; px < 256; px++) {
      var weave = (((px >> 1) + (py >> 1)) & 1) ? 7 : -7;
      var v = 122 + weave + (rnd() - 0.5) * 18 + Math.sin(py * 0.8) * 3, o = (py * 256 + px) * 4;
      img.data[o] = v; img.data[o + 1] = v + 2; img.data[o + 2] = v + 5; img.data[o + 3] = 255;
    }
    x.putImageData(img, 0, 0);
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
    return tex;
  }

  function boxData(w, h, d) {
    var x = w / 2, y = h / 2, z = d / 2;
    var p = [[-x, -y, -z], [x, -y, -z], [x, y, -z], [-x, y, -z], [-x, -y, z], [x, -y, z], [x, y, z], [-x, y, z]];
    var quads = [[4, 5, 6, 7], [1, 0, 3, 2], [5, 1, 2, 6], [0, 4, 7, 3], [7, 6, 2, 3], [0, 1, 5, 4]];
    var pos = [], idx = [];
    p.forEach(function (v) { pos.push(v[0], v[1], v[2]); });
    quads.forEach(function (q) { idx.push(q[0], q[1], q[2], q[0], q[2], q[3]); });
    return flatten(pos, idx, 1);
  }


  /* The card faces: assets/card-faces.webp holds the black, graphite and white sample cards stacked in thirds. */
  function loadCardAtlas(gl, url, done) {
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      var tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      var an = gl.getExtension('EXT_texture_filter_anisotropic');
      if (an) gl.texParameterf(gl.TEXTURE_2D, an.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, gl.getParameter(an.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));
      done(tex);
    };
    img.src = url;
  }

  function hexToRgb(hex) {
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
  }

  /* ----- shaders ----- */
  /* A black studio: low fill, one soft white key from above left, a cool-neutral back light that
     catches the edges, and a satin highlight like matte-printed PLA. */
  var VERT = 'attribute vec3 aPos;attribute vec3 aNor;uniform mat4 uProj;uniform mat4 uMv;varying vec3 vN;varying vec3 vP;varying float vY;varying vec3 vL;varying vec3 vNL;' +
    'void main(){vec4 p=uMv*vec4(aPos,1.0);vP=p.xyz;vY=aPos.y;vL=aPos;vNL=aNor;vN=(uMv*vec4(aNor,0.0)).xyz;gl_Position=uProj*p;}';
  var FRAG = 'precision mediump float;varying vec3 vN;varying vec3 vP;varying float vY;varying vec3 vL;varying vec3 vNL;uniform sampler2D uTex;uniform float uTexOn;uniform float uAlpha;uniform vec3 uColor;uniform float uMetal;uniform float uMirror;' +
    'void main(){' +
    'vec3 n=normalize(vN);vec3 v=normalize(-vP);' +
    'vec3 base=pow(uColor,vec3(2.2));' +
    /* printed card face: the +z face of a card box (85.6 x 53.98 mm, centred) takes row uTexOn (1 to 3) of the card atlas */
    'if(uTexOn>0.5&&vNL.z>0.5)base=pow(texture2D(uTex,vec2(vL.x/85.6+0.5,(uTexOn-0.5-vL.y/53.98)/3.0)).rgb,vec3(2.2));' +
    'if(uTexOn>4.5)base=pow(texture2D(uTex,(abs(vNL.y)>0.5?vL.xz:vL.xy)/30.0).rgb,vec3(2.2))*(abs(vNL.x)>0.5?0.88:1.0);' +
    'vec3 L1=normalize(vec3(-0.5,0.8,0.45));vec3 L2=normalize(vec3(0.85,0.25,0.3));vec3 L3=normalize(vec3(0.3,0.6,-0.75));vec3 L4=normalize(vec3(0.1,-1.0,0.35));' +
    'float key=max(dot(n,L1),0.0);float fill=max(dot(n,L2),0.0);' +
    'float sky=mix(0.03,0.12,n.y*0.5+0.5);' +
    'float s1=pow(max(dot(n,normalize(L1+v)),0.0),70.0)*0.32+pow(max(dot(n,normalize(L1+v)),0.0),8.0)*0.11;' +
    'float s2=pow(max(dot(n,normalize(L2+v)),0.0),24.0)*0.12;' +
    'float fr=pow(1.0-max(dot(n,v),0.0),4.0);' +
    'float back=max(dot(n,L3),0.0)*0.5+fr*0.35;' +
    'vec3 tint=mix(vec3(1.0),base/max(max(base.r,base.g),max(base.b,0.001)),0.85);' +
    /* white underlight: a cool-white bounce from the floor on the faces that look down */
    'float under=pow(max(dot(n,vec3(0.0,-1.0,0.15)),0.0),1.5);' +
    'float g=0.62+0.62*smoothstep(-70.0,70.0,-vP.x*0.9+vP.y*0.7);' +
    'vec3 c=base*(sky+1.05*key+0.28*fill)*g+tint*vec3(s1*1.3+s2*1.2)*(0.6+0.5*g)+tint*back*0.22+vec3(0.95,0.97,1.0)*under*0.55;' +
    /* studio reflections: a big soft box above left, a strip light on the right, a white floor below */
    'vec3 r=reflect(-v,n);' +
    'float e1=smoothstep(0.7,0.96,dot(r,normalize(vec3(-0.4,0.9,0.5))));' +
    'float e2=smoothstep(0.78,0.97,dot(r,normalize(vec3(0.95,0.25,0.15))))*0.8;' +
    'float e3=smoothstep(0.1,0.9,-r.y)*0.55;' +
    'float e4=smoothstep(0.82,0.99,dot(r,normalize(vec3(-0.9,0.1,-0.4))))*0.5;' +
    'vec3 env=vec3(1.0)*(e1*1.1+e2+e4)+vec3(0.95,0.97,1.0)*e3+vec3(0.04);' +
    'float refl=uMetal*(0.30+0.5*fr)+(1.0-uMetal)*(0.04+0.16*fr);' +
    'c+=env*mix(vec3(1.0),tint,uMetal*0.85)*refl*0.42;' +
    /* printed card faces read as matte printed plastic: mostly diffuse, a little sheen, so black cards stay black */
    'if(uTexOn>0.5&&vNL.z>0.5)c=mix(c,base*(0.3+0.95*key+0.3*fill)*g+vec3(s1*0.5),0.88);' +
    /* the visor's cloth: no gloss, just a soft sheen at grazing angles */
    'if(uTexOn>4.5)c=base*(0.4+0.85*key+0.3*fill)*g+vec3(0.9,0.92,0.95)*fr*0.06;' +
    'c=c/(1.0+c*0.35);' +
    'vec3 o=pow(c,vec3(1.0/2.2));' +
    'float fade=uMirror*0.11*pow(clamp(1.0-(vY+46.0)/26.0,0.0,1.0),1.8);' +
    'if(uMetal<0.0){float k=max(dot(n,L1),0.0);o=vec3(0.02+0.05*k);}' +
    'gl_FragColor=mix(vec4(o,1.0),vec4(o*fade,fade),uMirror)*uAlpha;}';
  /* The floor: a pool of light under the product with a soft contact shadow, drawn as a quad. */
  var FLOOR_VERT = 'attribute vec3 aPos;uniform mat4 uProj;uniform mat4 uMv;varying vec2 vQ;' +
    'void main(){vQ=aPos.xz;gl_Position=uProj*uMv*vec4(aPos,1.0);}';
  var FLOOR_FRAG = 'precision mediump float;varying vec2 vQ;' +
    'void main(){' +
    'float pool=1.0-smoothstep(0.0,1.0,length(vQ/vec2(125.0,85.0)));' +
    'float sh=1.0-smoothstep(0.15,1.0,length((vQ-vec2(0.0,-6.0))/vec2(70.0,30.0)));' +
    'float a=pool*pool*0.30*(1.0-0.9*sh);' +
    'gl_FragColor=vec4(vec3(1.0)*a,a);}';

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
  }

  function makeProgram(gl, vsrc, fsrc) {
    var vs = compile(gl, gl.VERTEX_SHADER, vsrc), fs = compile(gl, gl.FRAGMENT_SHADER, fsrc);
    if (!vs || !fs) return null;
    var p = gl.createProgram();
    gl.attachShader(p, vs); gl.attachShader(p, fs);
    gl.bindAttribLocation(p, 0, 'aPos'); gl.bindAttribLocation(p, 1, 'aNor');
    gl.linkProgram(p);
    return gl.getProgramParameter(p, gl.LINK_STATUS) ? p : null;
  }

  function initViewer(scope) {
    var el = scope.querySelector ? scope.querySelector('[data-v3d]') : null;
    if (!el || el.hasAttribute('data-ready')) return;

    var canvas = el.querySelector('[data-v3d-canvas]');
    var stage = el.querySelector('[data-v3d-stage]');
    var modelUrl = el.getAttribute('data-model');
    if (!canvas || !stage || !modelUrl) return;

    var gl = null;
    try { gl = canvas.getContext('webgl', { antialias: true, alpha: true, premultipliedAlpha: true }) || canvas.getContext('experimental-webgl'); } catch (e) { gl = null; }
    var program = gl && makeProgram(gl, VERT, FRAG);
    var floorProgram = gl && makeProgram(gl, FLOOR_VERT, FLOOR_FRAG);
    if (!program || !floorProgram) return;   // no WebGL: the still image stays in place
    el.setAttribute('data-ready', '');

    var slider = el.querySelector('[data-v3d-open]');
    var readout = el.querySelector('[data-v3d-readout]');
    var playBtn = el.querySelector('[data-v3d-play]');
    var presetBtns = Array.prototype.slice.call(el.querySelectorAll('[data-preset]'));

    var reduce = !motionOk();
    var FINAL = filmAt(FILM_END);
    var view = { rx: FINAL.rx, ry: FINAL.ry, zoom: 1, at: [0, 0, 0], open: 1, mount: 1, fold: 0 };
    var goal = { rx: view.rx, ry: view.ry, zoom: 1, open: 1 };
    var speed = { rx: 0, ry: 0, zoom: 0, open: 0 };
    var parts = [], cardAtlas = null, roofBuffer = null, roofVerts = 0, fabricTex = null, visorBuffer = null, visorVerts = 0, flapBuffer = null, flapVerts = 0, cardBuffer = null, cardVerts = 0, floorBuffer = null;
    var aspect = 1, distance = 300, dpr = 1;
    var film = -1;                         // seconds into the sequence; -1 when not playing
    var played = false, dragging = false, sliding = false, inView = false, seen = false, loaded = false, loading = false;
    var raf = 0, last = 0, vel = 0, lastX = 0, lastY = 0, lastT = 0;
    var still = /[?&]v3d-t=([\d.]+)/.exec(location.search);   // ?v3d-t=6 freezes the sequence at 6 s, for capturing stills

    /* ----- GL setup ----- */
    var uProj = gl.getUniformLocation(program, 'uProj');
    var uMv = gl.getUniformLocation(program, 'uMv');
    var uColor = gl.getUniformLocation(program, 'uColor');
    var uMetal = gl.getUniformLocation(program, 'uMetal');
    var uTexOn = gl.getUniformLocation(program, 'uTexOn');
    var uAlpha = gl.getUniformLocation(program, 'uAlpha');
    var uMirror = gl.getUniformLocation(program, 'uMirror');
    var fProj = gl.getUniformLocation(floorProgram, 'uProj');
    var fMv = gl.getUniformLocation(floorProgram, 'uMv');
    gl.enable(gl.CULL_FACE);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    function upload(data) {
      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      return buf;
    }

    function drawBuffer(buf, count, mv, color, metal, tex, alpha) {
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.enableVertexAttribArray(0); gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 24, 0);
      gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, 12);
      gl.uniformMatrix4fv(uMv, false, mv);
      gl.uniform3f(uColor, color[0] / 255, color[1] / 255, color[2] / 255);
      gl.uniform1f(uMetal, metal === undefined ? 1 : metal);
      gl.uniform1f(uAlpha, alpha === undefined ? 1 : alpha);
      var tx = tex === 5 ? fabricTex : cardAtlas;
      gl.uniform1f(uTexOn, tex && tx ? tex : 0);
      if (tex && tx) gl.bindTexture(gl.TEXTURE_2D, tx);
      gl.drawArrays(gl.TRIANGLES, 0, count);
    }

    function render() {
      if (!loaded) return;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      var angle = view.open * MAX_ANGLE * Math.PI / 180;
      // Keep the whole object centred as the cards fan out.
      var shift = -Math.max(0, cardLift(2, angle) - CARD_REST) / 2;
      var d = distance * view.zoom;
      var orbit = mul(trans(0, d * (aspect < 1.2 ? 0.08 : 0.04), -d), mul(rotX(view.rx * Math.PI / 180), rotY(view.ry * Math.PI / 180)));
      var cam = mul(orbit, trans(shift - view.at[0], -view.at[1], -view.at[2]));
      var proj = persp(30 * Math.PI / 180, aspect, d * 0.25, d * 3);

      // Floor first: no depth, blended over the clear canvas.
      gl.disable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE); gl.enable(gl.BLEND);
      gl.useProgram(floorProgram);
      gl.uniformMatrix4fv(fProj, false, proj);
      gl.uniformMatrix4fv(fMv, false, mul(cam, trans(0, -46, 0)));
      gl.bindBuffer(gl.ARRAY_BUFFER, floorBuffer);
      gl.enableVertexAttribArray(0); gl.disableVertexAttribArray(1);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 12, 0);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      gl.disable(gl.BLEND); gl.enable(gl.DEPTH_TEST); gl.enable(gl.CULL_FACE);
      gl.useProgram(program);
      gl.uniformMatrix4fv(uProj, false, proj);
      var turn = mul(trans(PIVOT[0], PIVOT[1], 0), mul(rotZ(-angle), trans(-PIVOT[0], -PIVOT[1], 0)));
      // the visor (with the U-dock on it) turns about its hinge; the case slides along its rail onto the dock
      var fold = foldMatrix(ease(view.fold)), caseOn = trans(CASE_SLIDE * (1 - ease(view.mount)), 0, 0);
      function drawModel(root) {
        var onVisor = mul(root, fold), inCase = mul(onVisor, caseOn);
        parts.forEach(function (p) {
          var m = p.key === 'hook' ? onVisor : (p.key === 'lever' || p.key === 'comb') ? mul(inCase, turn) : inCase;
          drawBuffer(p.buf, p.count, m, p.color, p.metal);
        });
        for (var i = 0; i < 3; i++) {
          var x = (16 + 101.6) / 2 - 52 + cardLift(i, angle);
          var z = 4.4 - (3 + 2 * i);
          drawBuffer(cardBuffer, cardVerts, mul(inCase, trans(x, 0, z)), CARD_COLORS[i], 0.12, i + 1);
        }
        drawBuffer(visorBuffer, visorVerts, onVisor, VISOR_COLOR, 0.04, 5);
        drawBuffer(flapBuffer, flapVerts, onVisor, FLAP_COLOR, 0.35);
      }
      // Reflection on the floor: the model mirrored about the floor plane, faded with height, drawn before the model itself.
      var flip = ident(); flip[5] = -1;
      var mirror = mul(trans(0, -46, 0), mul(flip, trans(0, 46, 0)));
      gl.enable(gl.BLEND); gl.frontFace(gl.CW); gl.uniform1f(uMirror, 1);
      drawModel(mul(cam, mirror));
      gl.uniform1f(uMirror, 0); gl.frontFace(gl.CCW); gl.disable(gl.BLEND); gl.clear(gl.DEPTH_BUFFER_BIT);
      drawModel(cam);
      // the roof lining (the same cloth as the visor) fades in only while the visor folds up, so it never clutters the other shots
      var roofIn = Math.min(1, view.fold * 4);
      if (roofIn > 0.002) { gl.enable(gl.BLEND); drawBuffer(roofBuffer, roofVerts, cam, ROOF_COLOR, 0.02, 5, roofIn); gl.disable(gl.BLEND); }
      var deg = Math.round(view.open * MAX_ANGLE);
      if (!sliding) {
        if (slider) { slider.value = deg; slider.style.setProperty('--p', (deg / MAX_ANGLE * 100) + '%'); }
        if (readout) readout.textContent = deg + '°';
      }
    }

    function request() { if (!raf) raf = window.requestAnimationFrame(tick); }

    function tick(t) {
      raf = 0;
      // Capped so a stalled frame (a busy or background tab) catches up instead of jumping.
      var dt = Math.min(0.1, ((t - (last || t)) / 1000));
      last = t;
      var busy = false;

      if (film >= 0) {
        if (inView && !still) film += dt * FILM_SPEED;   // the sequence runs a little faster than real time
        var f = filmAt(Math.min(film, FILM_END));
        view.rx = f.rx; view.ry = f.ry; view.zoom = f.zoom; view.at = f.at; view.open = f.open; view.mount = f.mount; view.fold = f.fold;
        if (film >= FILM_END && !still) endFilm(); else busy = inView && !still;
      } else if (!dragging) {
        // Critically damped follow: moves settle without overshoot.
        if (reduce) {
          view.rx = goal.rx; view.ry = goal.ry; view.zoom = goal.zoom; view.open = goal.open;
        } else {
          for (var left = dt; left > 0; left -= 1 / 120) {
            var h = Math.min(left, 1 / 120);
            ['rx', 'ry', 'zoom', 'open'].forEach(function (key) {
              var stiff = key === 'open' ? 60 : 80, damp = 2 * Math.sqrt(stiff);
              speed[key] += ((goal[key] - view[key]) * stiff - speed[key] * damp) * h;
              view[key] += speed[key] * h;
            });
          }
          ['rx', 'ry', 'zoom', 'open'].forEach(function (key) {
            if (Math.abs(goal[key] - view[key]) > (key === 'rx' || key === 'ry' ? 0.02 : 0.001) || Math.abs(speed[key]) > 0.01) busy = true;
            else { view[key] = goal[key]; speed[key] = 0; }
          });
        }
        // Ease the look-at point back to the product's centre after the sequence.
        var k = reduce ? 1 : 1 - Math.exp(-dt * 6);
        for (var a = 0; a < 3; a++) { view.at[a] += (0 - view.at[a]) * k; if (Math.abs(view.at[a]) > 0.05) busy = true; else view.at[a] = 0; }
      }

      render();
      if (busy || dragging) request(); else last = 0;
    }

    /* ----- the sequence ----- */
    function setPlayLabel() {
      if (!playBtn) return;
      playBtn.textContent = film >= 0 ? playBtn.dataset.pause : (played ? playBtn.dataset.replay : playBtn.dataset.play);
      playBtn.setAttribute('aria-pressed', film >= 0 ? 'true' : 'false');
    }
    function startFilm() {
      film = 0; played = true; last = 0;
      el.classList.add('is-playing');
      setPlayLabel();
      request();
    }
    function endFilm() {
      if (film < 0) return;
      film = -1;
      goal.rx = view.rx; goal.ry = view.ry; goal.zoom = 1; goal.open = view.open;
      speed.rx = speed.ry = speed.zoom = speed.open = 0;
      el.classList.remove('is-playing');
      setPlayLabel();
      request();
    }
    /* Anything the visitor does takes over from the sequence at the frame it has reached. */
    function takeover() { endFilm(); played = true; }

    function go(name) {
      var v = VIEWS[name];
      if (!v) return;
      takeover();
      goal.rx = v.rx;
      goal.ry = view.ry + wrap180(v.ry - view.ry);
      goal.zoom = 1;
      goal.open = v.open;
      request();
    }

    /* ----- pointer drag to orbit ----- */
    stage.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      dragging = true;
      takeover();
      speed.rx = speed.ry = 0;
      stage.classList.add('is-drag');
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      lastX = e.clientX; lastY = e.clientY; lastT = e.timeStamp; vel = 0;
      request();
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      view.ry += dx * 0.5;
      if (e.pointerType === 'mouse') view.rx = clamp(view.rx + dy * 0.35, -80, 25);
      goal.rx = view.rx; goal.ry = view.ry;
      var dtm = Math.max(1, e.timeStamp - lastT);
      vel = vel * 0.8 + (dx * 0.5 / dtm) * 0.2;
      lastT = e.timeStamp;
      request();
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('is-drag');
      goal.ry = view.ry + clamp(vel * 60, -30, 30);   // a short glide, never a free spin
      request();
    }
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);

    /* ----- keyboard ----- */
    stage.addEventListener('keydown', function (e) {
      var handled = true;
      if (e.key === 'ArrowLeft') { takeover(); goal.ry -= 8; }
      else if (e.key === 'ArrowRight') { takeover(); goal.ry += 8; }
      else if (e.key === 'ArrowUp') { takeover(); goal.rx = clamp(goal.rx - 5, -80, 25); }
      else if (e.key === 'ArrowDown') { takeover(); goal.rx = clamp(goal.rx + 5, -80, 25); }
      else if (e.key === 'Home') { go('open'); }
      else { handled = false; }
      if (handled) { e.preventDefault(); request(); }
    });

    /* ----- controls ----- */
    if (slider) {
      slider.setAttribute('max', MAX_ANGLE);
      slider.addEventListener('input', function () {
        takeover();
        sliding = true;
        view.open = goal.open = slider.value / MAX_ANGLE;
        slider.style.setProperty('--p', (slider.value / MAX_ANGLE * 100) + '%');
        if (readout) readout.textContent = slider.value + '°';
        request();
      });
      slider.addEventListener('change', function () { sliding = false; });
    }
    presetBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { go(btn.getAttribute('data-preset')); });
    });
    if (playBtn) {
      playBtn.addEventListener('click', function () { if (film >= 0) takeover(); else startFilm(); });
      setPlayLabel();
    }

    /* ----- sizing and visibility ----- */
    function fit() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      var box = canvas.parentNode, w = box.clientWidth, h = box.clientHeight;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      aspect = w / Math.max(h, 1);
      var half = 15 * Math.PI / 180;                       // half of the 30 degree field of view
      var halfH = Math.atan(Math.tan(half) * aspect);
      // Fit the model's height (62 mm radius) and its width with the cards fanned out (78 mm half-width), with room around it.
      distance = Math.max(72 / Math.sin(half), 96 / Math.sin(halfH));
      request();
    }

    /* ----- load the model: only as the section comes near, so it never holds up the page ----- */
    function load() {
      if (loading) return;
      loading = true;
      fetch(modelUrl).then(function (r) { if (!r.ok) throw new Error('model'); return r.json(); }).then(function (data) {
        data.parts.forEach(function (p) {
          var flat = flatten(b64(p.pos, Int16Array), b64(p.idx, Uint16Array), data.unit);
          if (p.key === 'housing') {
            // The recessed lettering is part of the housing mesh, in the two underside pockets: its floor (z 3.55) and walls (3.55 to 3.8) inside each lettering band are drawn in black.
            var keep = [], ink = [];
            for (var t = 0; t < flat.length; t += 18) {
              var zs = [flat[t + 2], flat[t + 8], flat[t + 14]], xs = [flat[t], flat[t + 6], flat[t + 12]], ys = [flat[t + 1], flat[t + 7], flat[t + 13]];
              var zlo = Math.min(zs[0], zs[1], zs[2]), zhi = Math.max(zs[0], zs[1], zs[2]);
              var ylo = Math.min(ys[0], ys[1], ys[2]), yhi = Math.max(ys[0], ys[1], ys[2]);
              var inBand = Math.min(xs[0], xs[1], xs[2]) > -20 && Math.max(xs[0], xs[1], xs[2]) < 26 && ((ylo > 16 && yhi < 23) || (ylo > -23 && yhi < -16));
              var isInk = inBand && zlo > 3.5 && zhi < 3.85 && (zhi - zlo > 0.2 || zhi < 3.6);
              if (!isInk) { keep.push.apply(keep, flat.subarray(t, t + 18)); continue; }
              if (FLUSH_LETTERING) {
                // The CAD still carries the old VISORDOCK lettering: drop its walls and lift its floor to the face, so the face reads plain.
                if (zhi - zlo > 0.3) continue;
                var tri = flat.slice(t, t + 18); tri[2] = tri[8] = tri[14] = 3.8;
                keep.push.apply(keep, tri);
              } else {
                ink.push.apply(ink, flat.subarray(t, t + 18));
              }
            }
            if (ink.length || FLUSH_LETTERING) {
              var lettering = new Float32Array(ink);
              if (ink.length) parts.push({ key: 'lettering', color: LETTER_COLOR, buf: upload(lettering), count: lettering.length / 6, metal: -1 });
              flat = new Float32Array(keep);
            }
          }
          parts.push({ key: p.key, color: PART_COLORS[p.key] || hexToRgb(p.color), buf: upload(flat), count: flat.length / 6 });
        });
        var card = roundedCardData(CARD_SIZE[0], CARD_SIZE[1], CARD_SIZE[2], 3.18);
        // padded edges: a 14 mm core plus two 3 mm layers stepped in 2.5 mm, so the rim reads soft rather than cut
        var vx = -26 + VISOR[0] / 2, vy = -42 + VISOR[1] / 2, vz = -7.2 - VISOR[2] / 2;
        var layers = [moved(roundedCardData(VISOR[0], VISOR[1], VISOR[2] - 6, 24), vx, vy, vz),
          moved(roundedCardData(VISOR[0] - 5, VISOR[1] - 5, 3, 21.5), vx, vy, vz + VISOR[2] / 2 - 1.5),
          moved(roundedCardData(VISOR[0] - 5, VISOR[1] - 5, 3, 21.5), vx, vy, vz - VISOR[2] / 2 + 1.5)];
        var visor = new Float32Array(layers.reduce(function (s, l) { return s + l.length; }, 0)), vo = 0;
        layers.forEach(function (l) { visor.set(l, vo); vo += l.length; });
        var flap = moved(roundedCardData(112, 64, 2.2, 7), 175, 40, -6.1);
        visorBuffer = upload(visor); visorVerts = visor.length / 6;
        flapBuffer = upload(flap); flapVerts = flap.length / 6;
        fabricTex = fabricTexture(gl);
        var roof = roofData(); roofBuffer = upload(roof); roofVerts = roof.length / 6;
        cardBuffer = upload(card);
        cardVerts = card.length / 6;
        if (el.getAttribute('data-cards')) loadCardAtlas(gl, el.getAttribute('data-cards'), function (tex) { cardAtlas = tex; request(); });
        var s = 200;
        floorBuffer = upload(new Float32Array([-s, 0, -s, s, 0, s, s, 0, -s, -s, 0, -s, -s, 0, s, s, 0, s]));
        loaded = true;
        el.classList.add('is-loaded');
        if (still) { film = parseFloat(still[1]); inView = false; played = true; }
        fit();
        maybePlay();
      }).catch(function () { el.setAttribute('data-failed', ''); });
    }

    /* Plays once, the first time most of the stage is on screen, unless the visitor prefers reduced motion. */
    function maybePlay() {
      if (loaded && seen && !played && !reduce) startFilm();
      else request();
    }

    if ('ResizeObserver' in window) new ResizeObserver(fit).observe(canvas.parentNode);
    else window.addEventListener('resize', fit);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { if (entries[0].isIntersecting) load(); }, { rootMargin: '600px 0px' }).observe(el);
      new IntersectionObserver(function (entries) {
        var e = entries[0];
        inView = e.isIntersecting;
        if (e.intersectionRatio >= 0.55) seen = true;
        if (inView) maybePlay();
      }, { threshold: [0, 0.55] }).observe(stage);
    } else {
      inView = seen = true;
      load();
    }
  }

  function initAll(scope) {
    initViewer(scope);
  }

  function init() {
    initAll(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
